import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  input,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ListItem } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  AbEntryService,
  NavigationService,
  OutlookStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookAbEntryDetails,
  OutlookAbEntryForm,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
  PHONE_VALIDATORS,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { catchError, finalize, forkJoin, of } from 'rxjs';

@Component({
  selector: 'maximizer-edit-abentry',
  templateUrl: './edit-abentry.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIP,
    NgClass,
    NgTemplateOutlet,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class EditAbEntryComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  id = input.required<string>();

  abEntryKey = computed(() => {
    return decodeURI(this.id());
  });

  type = signal<EntryType>(EntryType.Contact);

  EntryType = EntryType;
  loading = true;
  abentry?: OutlookAbEntryDetails;
  phones: { type: string; value: string }[] = [];
  emails: { type: string; value: string }[] = [];
  emailOptions: string[] = [];
  phoneOptions: string[] = [];
  countries: ListItem<string>[] = [];
  phoneLimit = 4;
  emailLimit = 3;
  form?: FormGroupWrapper<OutlookAbEntryForm>;
  entryNotification?: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly abentryService: AbEntryService,
    private readonly navigationService: NavigationService,
    private readonly tracker: TrackerService,
    public outlookStore: OutlookStore,
  ) {}

  ngOnInit(): void {
    const typeParam = this.route.snapshot.queryParamMap.get('entryType');

    if (!typeParam) {
      this.navigationService.navigateBack();
      return;
    }

    this.type.set(typeParam as EntryType);

    if (this.abEntryKey()) {
      this.loadData();
    }
  }

  private loadData(): void {
    forkJoin({
      abentry: this.abentryService.getAllDetailsByKey(this.abEntryKey()),
      fieldOptions: this.abentryService.getFieldOption(),
    })
      .pipe(
        catchError(async () => {
          this.notification().show('load', 'error');
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((data) => {
        if (!data) return;
        if (!data.abentry) return;

        this.abentry = data.abentry;

        this.phoneOptions = data.fieldOptions.phoneDescriptions.map(
          (phone) => phone.id,
        );
        this.emailOptions = data.fieldOptions.emailDescriptions.map(
          (email) => email.id,
        );
        this.countries = data.fieldOptions.addressCountry;
        this.setForm(data.abentry);
      });
  }

  private setForm(abentry: OutlookAbEntryDetails): void {
    this.prepareDynamicFields(abentry);
    const disableAddress = this.type() === EntryType.Contact;

    this.form = FormWrapperBuilder.group<OutlookAbEntryForm>({
      key: abentry.key,
      firstName: [abentry.firstName, [Validators.maxLength(79)]],
      lastName: [abentry.lastName, [Validators.maxLength(79)]],

      email1: [this.emails[0]?.value, []],
      email1Description: [this.emails[0]?.type, [Validators.maxLength(15)]],
      email2: [
        this.emails[1]?.value,
        {
          validators: [Validators.maxLength(255), Validators.email],
          updateOn: 'blur',
        },
      ],
      email2Description: [this.emails[1]?.type, [Validators.maxLength(15)]],
      email3: [
        this.emails[2]?.value,
        {
          validators: [Validators.maxLength(255), Validators.email],
          updateOn: 'blur',
        },
      ],
      email3Description: [this.emails[2]?.type, [Validators.maxLength(15)]],
      phone1: [this.phones[0]?.value, PHONE_VALIDATORS],
      phone1Description: [this.phones[0]?.type, [Validators.maxLength(21)]],
      phone2: [this.phones[1]?.value, PHONE_VALIDATORS],
      phone2Description: [this.phones[1]?.type, [Validators.maxLength(21)]],
      phone3: [this.phones[2]?.value, PHONE_VALIDATORS],
      phone3Description: [this.phones[2]?.type, [Validators.maxLength(21)]],
      phone4: [this.phones[3]?.value, PHONE_VALIDATORS],
      phone4Description: [this.phones[3]?.type, [Validators.maxLength(21)]],
      position: [abentry.position, [Validators.maxLength(79)]],
      website: [abentry.website, [Validators.maxLength(255)]],
      company: [
        abentry?.type === EntryType.Contact
          ? abentry.parentDisplayValue
          : abentry.companyName,
        [Validators.maxLength(79)],
      ],
      address1: [
        { value: abentry.address?.address1, disabled: disableAddress },
        [Validators.maxLength(79)],
      ],
      address2: [
        { value: abentry.address?.address2, disabled: disableAddress },
        [Validators.maxLength(79)],
      ],
      city: [
        { value: abentry.address?.city, disabled: disableAddress },
        [Validators.maxLength(79)],
      ],
      province: [
        { value: abentry.address?.provinceState, disabled: disableAddress },
        [Validators.maxLength(79)],
      ],
      postalCode: [
        { value: abentry.address?.zipcode, disabled: disableAddress },
        [Validators.maxLength(79)],
      ],
      country: [
        { value: null, disabled: disableAddress },
        [Validators.maxLength(79)],
      ],
      addressKey: abentry.address?.key,
      type: abentry.type,
      parentKey: abentry.parentKey,
    });

    if (abentry.address?.country) {
      this.form.group.patchValue({
        country: abentry.address.country,
      });
    }

    this.form.validate();
  }

  private prepareDynamicFields(abentry: OutlookAbEntryDetails): void {
    for (const phone of abentry.phones) {
      if (phone.value.trim() == '') continue;
      this.phones.push({
        type: phone?.type ?? '',
        value: phone?.value ?? '',
      });
    }
    if (this.phones.length == 0) {
      this.phones.push({ type: '', value: '' });
    }

    for (const email of abentry.emails) {
      if (email.value.trim() == '') continue;
      this.emails.push({
        type: email?.type.trim() ?? '',
        value: email?.value.trim() ?? '',
      });
    }
    if (this.emails.length == 0) {
      this.emails.push({ type: '', value: '' });
    }
  }

  save(): void {
    if (!this.form) {
      return;
    }

    const validation = this.form.validate();
    if (!validation.valid) return;

    this.form.patch({
      key: this.abEntryKey(),
    });

    this.loading = true;
    this.abentryService
      .update(this.form.value)
      .pipe(
        catchError(() => {
          this.notification().show('save', 'error');
          return of(false);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((result) => {
        const success = !!result;

        this.tracker.trackEvent({
          module: EventModule.Entry,
          journey: EventJourney.Update,
          action: EventAction.Completed,
          metadata: {
            entry: this.type(),
            result: success ? EventMetadata.Success : EventMetadata.Failed,
          },
        });

        if (success) {
          this.notification().show('save', 'success', false);
          this.navigationService.navigateBack();
        }
      });
  }

  cancel(): void {
    this.navigationService.navigateBack();
  }

  onEntryChange(event: string, prop: string, index: number): void {
    const array = prop.includes('email') ? this.emails : this.phones;
    if (prop.includes('Description')) {
      const limitProp = prop.includes('email') ? 16 : 22;
      let eventLimited = event;
      if (event.length >= limitProp) {
        eventLimited = event.slice(0, limitProp);
        this.form?.patch({
          [prop]: eventLimited,
        });
      }
      array[index].type = eventLimited.trim();
    } else {
      array[index].value = event.trim();
    }
  }

  onRemoveEntry(i: number, prop: string): void {
    if (prop.includes('email')) {
      this.emails.splice(i, 1);
      const array = structuredClone(this.emails); // update form reference
      this.emails = array;
      this.updateEmails();
    } else {
      const array = structuredClone(this.phones); // update form reference
      array.splice(i, 1);
      this.phones = array;
      this.updatePhones();
    }
  }

  onAddEntry(type: string): void {
    if (type.includes('email')) {
      this.emails.push({ type: '', value: '' });
    } else {
      this.phones.push({
        type: '',
        value: '',
      });
    }
  }

  private updateEmails(): void {
    this.form?.group.patchValue({
      email1: this.emails[0]?.value.trim() ?? '',
      email1Description: this.emails[0]?.type.trim() ?? '',

      email2: this.emails[1]?.value.trim() ?? '',
      email2Description: this.emails[1]?.type.trim() ?? '',

      email3: this.emails[2]?.value.trim() ?? '',
      email3Description: this.emails[2]?.type.trim() ?? '',
    });
  }

  private updatePhones(): void {
    this.form?.patch({
      phone1: this.phones[0]?.value.trim() ?? '',
      phone1Description: this.phones[0]?.type.trim() ?? '',
      phone2: this.phones[1]?.value.trim() ?? '',
      phone2Description: this.phones[1]?.type.trim() ?? '',
      phone3: this.phones[2]?.value.trim() ?? '',
      phone3Description: this.phones[2]?.type.trim() ?? '',
      phone4: this.phones[3]?.value.trim() ?? '',
      phone4Description: this.phones[3]?.type.trim() ?? '',
    });
  }

  getFieldLimit(prop: string): number {
    if (prop.includes('email')) return this.emailLimit;
    return this.phoneLimit;
  }

  getEmailErrorObject(prop: string): ValidationErrors | null | undefined {
    return this.form?.group.controls[prop].errors;
  }
}
